import { toast } from 'react-toastify';
import { IOrder } from '@monofront/types';
import { ModalConfirmation } from '@components/ModalConfirmation';
import React from 'react';
import { useMutation } from '@apollo/client';
import { CANCEL_ORDER } from '@monofront/business/queries';
import { useTranslations } from 'next-intl';

interface ICancelOrderModalConfirmationProps {
  order: IOrder;
  isOpen: boolean;
  hide: () => void;
  onCancelSuccess: (order: IOrder) => void | Promise<void>;
}

export const CancelOrderModalConfirmation: React.FC<
  ICancelOrderModalConfirmationProps
> = ({ order, isOpen, hide, onCancelSuccess }) => {
  const [cancelOrderRequest] = useMutation<{ cancelOrder: IOrder }>(
    CANCEL_ORDER,
  );

  const tAccount = useTranslations('Account');
  const tErrors = useTranslations('Errors');

  const cancelOrder = async () => {
    try {
      const result = await cancelOrderRequest({
        variables: {
          orderId: order.id,
        },
      });

      if (result.data) {
        onCancelSuccess(result.data.cancelOrder);
      }
      toast(tAccount('orderCanceled', { orderName: order.name }));
    } catch (e: any) {
      toast(tErrors('orderCancellationError', { orderName: order.name }), {
        progressClassName: 'toast-error',
      });
    } finally {
      hide();
    }
  };

  return (
    <>
      <ModalConfirmation
        title={tAccount('orderCancelConfirmationTitle')}
        message={tAccount.rich('orderCancelConfirmationDetails', {
          a: (children) => (
            <a
              href="https://support.lafourche.fr/hc/fr/requests/new"
              target="_blank"
              className="tw-lf-link-inherit"
            >
              {children}
            </a>
          ),
        })}
        isOpen={isOpen}
        confirmButtonLabel={tAccount('orderCancelConfirmationCTA')}
        confirmButtonOnClick={cancelOrder}
        cancelButtonOnClick={hide}
        modalClassName="cancel-order-modal"
      />
      <style jsx>{`
        :global(.cancel-order-modal) {
          top: 33%;
          max-width: 560px;
        }

        @media (max-width: 800px) {
          :global(.cancel-order-modal) {
            top: 20%;
          }

          :global(.cancel-order-modal) :global(.modal-cta) {
            width: 100%;
            flex-direction: column;
            align-items: center;
          }

          :global(.cancel-order-modal) :global(.modal-cta > button:last-child) {
            margin-top: var(--space-3);
            white-space: nowrap;
          }

          :global(.cancel-order-modal) :global(a) {
            margin-left: 0;
            margin-top: var(--space-4);
          }
        }
      `}</style>
    </>
  );
};
